import { api, endpoints } from "api/api";
import { CategoryApi } from "api/categories";
import { CourseApi, CourseData } from "api/courses/coursesApi";
import { BaseApiPaging } from "api/types";
import { AxiosPromise } from "axios";
import { CourseGroupFormSchema } from "pages/CourseGroup/CourseGroupBaseForm";

// All HTTP requests use the imported 'api' instance for consistency
const fetch = (
  skip: number,
  take: number,
  filter: string = "",
  orderBy: string = "",
): AxiosPromise<BaseApiPaging<CourseGroupApi>> =>
  api.get<BaseApiPaging<CourseGroupApi>>(endpoints.COURSEGROUPS.GET(skip, take, filter, orderBy));

const fetchById = (courseGroupId: number): AxiosPromise<CourseGroupApi> =>
  api.get<CourseGroupApi>(endpoints.COURSEGROUPS.GET_BY_ID(courseGroupId));

const fetchCourses = (courseGroupId: number): AxiosPromise<{ courses: CourseData[] }> =>
  api.get<{ courses: CourseData[] }>(endpoints.COURSEGROUPS.GET_COURSES(courseGroupId));

const deleteGroup = (courseGroupId: number): Promise<void> => api.delete(endpoints.COURSEGROUPS.DELETE(courseGroupId));

const updateField = (
  courseGroupId: number,
  courseGroupFieldId: number,
  data: { name: string; content?: string | null },
): AxiosPromise =>
  api.put(endpoints.COURSEGROUPS.UPDATE_FIELD(courseGroupId, courseGroupFieldId), {
    ...data,
    content: data.content ?? "",
  });

const updateContactPersons = (
  courseGroupId: number,
  data: { area: string; phone: string; email: string; userId: number }[],
): AxiosPromise => api.put(endpoints.COURSEGROUPS.UPDATE_CONTACT_PERSONS(courseGroupId), { contactPersons: data });

const createField = (
  courseGroupId: number,
  data: { name: string; content?: string | null; type: number },
): AxiosPromise<number> =>
  api.post(endpoints.COURSEGROUPS.CREATE_FIELD(courseGroupId), { ...data, content: data.content ?? "" });

const deleteField = (courseGroupId: number, courseGroupFieldId: number): AxiosPromise =>
  api.delete(endpoints.COURSEGROUPS.DELETE_FIELD(courseGroupId, courseGroupFieldId));

const create = (data: CourseGroupFormSchema): AxiosPromise<number> => {
  const { mainImage } = data;
  const formData = new FormData();
  const request = {
    name: data.name,
    categoryId: data.categoryId,
    ogImageAlt: data.ogImageAlt,
    ogDescription: data.ogDescription,
    slug: data.slug,
    onlineMeetingUrl: data.onlineMeetingUrl,
    onlineMeetingPassword: data.onlineMeetingPassword,
    trainerOnlineMeetingUrl: data.trainerOnlineMeetingUrl,
  };

  // @ts-ignore TODO
  if (mainImage && typeof mainImage !== "string") formData.append("mainImage", mainImage[0] as File);

  Object.entries(request).map(([key, value]) => {
    if (!value) return;
    //@ts-ignore
    formData.append(key, value);
  });
  return api.post(endpoints.COURSEGROUPS.CREATE(), formData);
};

const update = (courseGroupId: number, data: CourseGroupFormSchema): AxiosPromise => {
  const { mainImage } = data;
  const formData = new FormData();
  const request = {
    name: data.name,
    categoryId: data.categoryId,
    ogImageAlt: data.ogImageAlt,
    ogDescription: data.ogDescription,
    slug: data.slug,
    onlineMeetingUrl: data.onlineMeetingUrl,
    onlineMeetingPassword: data.onlineMeetingPassword,
    trainerOnlineMeetingUrl: data.trainerOnlineMeetingUrl,
  };

  // @ts-ignore TODO
  if (mainImage && typeof mainImage !== "string") formData.append("mainImage", mainImage[0] as File);

  Object.entries(request).map(([key, value]) => {
    if (!value) return;
    //@ts-ignore
    formData.append(key, value);
  });
  return api.put(endpoints.COURSEGROUPS.UPDATE(courseGroupId), formData);
};

const fetchTrainers = (courseGroupId: number): AxiosPromise<GetCourseGroupTrainersResponse> =>
  api.get<GetCourseGroupTrainersResponse>(endpoints.COURSEGROUPS.GET_TRAINERS(courseGroupId));

const updateBlacklistedTrainers = (courseGroupId: number, data: UpdateBlacklistedTrainersBody): AxiosPromise =>
  api.put(endpoints.COURSEGROUPS.UPDATE_BLACKLISTED_TRAINERS(courseGroupId), data);

const getProgram = (courseGroupId: number): AxiosPromise<ProgramModuleApi[]> =>
  api.get<ProgramModuleApi[]>(endpoints.COURSEGROUPS.GET_PROGRAM(courseGroupId));

const updateProgram = (courseGroupId: number, program: ProgramModuleApi[]): AxiosPromise =>
  api.put(endpoints.COURSEGROUPS.UPDATE_PROGRAM(courseGroupId), program);

const prepopulateProgram = (courseGroupId: number, sourceCourseId: number): AxiosPromise =>
  api.post(endpoints.COURSEGROUPS.PREPOPULATE_PROGRAM(courseGroupId), { sourceCourseId });

const getFaq = (courseGroupId: number): AxiosPromise<FaqItemApi[]> =>
  api.get<FaqItemApi[]>(endpoints.COURSEGROUPS.GET_FAQ(courseGroupId));

const updateFaq = (courseGroupId: number, faq: FaqItemApi[]): AxiosPromise =>
  api.put(endpoints.COURSEGROUPS.UPDATE_FAQ(courseGroupId), faq);

export interface BulkAssignCategoryRequest {
  courseGroupIds: number[];
  categoryId: number;
}

const bulkAssignCategory = (data: BulkAssignCategoryRequest): AxiosPromise<void> =>
  api.post(endpoints.COURSEGROUPS.BULK_ASSIGN_CATEGORY(), data);

const fetchImageSizes = (): AxiosPromise<CourseGroupImageSizeApi[]> =>
  api.get<CourseGroupImageSizeApi[]>(endpoints.COURSEGROUPS.GET_IMAGE_SIZES());

export interface CourseGroupShopSettingsApi {
  courseGroupId: number;
  name: string;
  hideScheduleTab: boolean;
  scheduleTabCustomText?: string;
  hidePrice: boolean;
  productCardCustomText?: string;
  showOngoingCourses: boolean;
}

export interface CourseGroupWaitlistEntryApi {
  id: number;
  name: string;
  email: string;
  createdDate: Date;
}

const getShopSettings = (): AxiosPromise<CourseGroupShopSettingsApi[]> =>
  api.get(endpoints.COURSEGROUPS.SHOP_SETTINGS());

const updateShopSettings = (
  courseGroupId: number,
  data: {
    hideScheduleTab: boolean;
    hidePrice: boolean;
    scheduleTabCustomText?: string;
    productCardCustomText?: string;
    showOngoingCourses: boolean;
  },
): AxiosPromise => api.put(endpoints.COURSEGROUPS.UPDATE_SHOP_SETTINGS(courseGroupId), data);

const getWaitlist = (courseGroupId: number): AxiosPromise<CourseGroupWaitlistEntryApi[]> =>
  api.get(endpoints.COURSEGROUPS.WAITLIST(courseGroupId));

export const coursegroupsApi = {
  fetch,
  fetchById,
  update,
  fetchCourses,
  create,
  updateField,
  createField,
  deleteField,
  delete: deleteGroup,
  updateContactPersons,
  fetchTrainers,
  updateBlacklistedTrainers,
  getProgram,
  updateProgram,
  prepopulateProgram,
  getFaq,
  updateFaq,
  bulkAssignCategory,
  fetchImageSizes,
  getShopSettings,
  updateShopSettings,
  getWaitlist,
};

export interface CourseGroupAttributeValueListApi {
  attributeId: number;
  boolValue: boolean | null;
  textValue: string | null;
  selectedOptionValues: string[] | null;
}

export interface CourseGroupApi {
  name: string;
  slug: string;
  id: number;
  mainImage: string;
  category: CategoryApi;
  courses?: CourseApi[];
  contactPeople: CourseGroupContactPersonApi[];
  fields: CourseGroupFieldApi[];
  blacklistedTrainerIds?: string;
  attributeValues?: CourseGroupAttributeValueListApi[];
  ogImageAlt?: string;
  ogDescription?: string;
  onlineMeetingUrl?: string | null;
  onlineMeetingPassword?: string | null;
  trainerOnlineMeetingUrl?: string | null;
}

export interface CourseGroupContactPersonApi {
  area: string;
  phone: string;
  email: string;
  userId: number;
}

export interface CourseGroupFieldApi {
  courseGroupFieldId: number;
  name: string;
  createdDate: Date;
  content: string;
  type: number;
  icon?: string;
}

export interface TrainerDto {
  id: number;
  firstName: string;
  surname: string;
  email: string;
}

export interface GetCourseGroupTrainersResponse {
  trainers: TrainerDto[];
}

export interface UpdateBlacklistedTrainersBody {
  blacklistedTrainerIds?: string;
}

export const COURSE_GROUP_FIELD_TYPE = {
  DESCRIPTION: 1,
  DETAILED_INFORMATION: 2,
  AGENDA: 4,
  AGENDA_TAG: 5,
} as const;

export interface FaqItemApi {
  question: string;
  answer: string;
}

export interface ProgramModuleApi {
  name: string;
  icon: string | null;
  content: string | null;
  tags: string[];
}

export interface CourseGroupImageSizeApi {
  id: number;
  name: string;
  mainImage: string | null;
  sizeBytes: number | null;
  missing: boolean;
}
